import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Group, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { BRAND_COLOR_ROLES, PAGE_TYPES } from '@seo/contracts';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import classes from './EntityPage.module.css';

/**
 * Склад бренду: кольори, соцмережі й типи сторінок.
 *
 * Три таблиці, які **лише читались**. `computeFill` рахує кольори (10), медіа (15) і типи
 * сторінок (5) — 30 ваги зі 100, — а записати в них не було чим ні з API, ні з екрана. Бренд,
 * заведений через CRM, набирав щонайбільше 70: рівно типовий поріг збірки. Тобто «наповнення
 * з нуля через саму CRM» трималося на тому, що всі шість решти компонентів заповнені
 * бездоганно.
 *
 * Набори, а не списки: `brand_colors` має унікальний індекс на (бренд, роль), `brand_page_types`
 * — на (бренд, тип). Кожне збереження замінює вміст цілком, і екран показує це прямо —
 * оператор редагує набір, а не додає рядки, які потім не знає, як прибрати.
 *
 * Медіа тут немає: слот заповнюється завантаженням файлу, і привʼязка `brand_assets` — окрема
 * робота Етапу 4. Поки що це видима стеля 85 зі 100.
 */

export interface BrandCompositionProps {
  brandId: string;
  /**
   * Які частини складу малювати.
   *
   * Картка бренду розʼїхалась на пʼять вкладок мокапа, і кольори з соцмережами живуть на
   * «Медіа», а типи сторінок — на «Сторінках». Прапорець замість трьох компонентів: усі три
   * набори зберігаються однаково (заміна цілком), і розділяти їх на файли означало б
   * тримати цю однаковість у трьох місцях.
   */
  sections?: ('colors' | 'socials' | 'pageTypes')[];
  colors: { id: string; role: string; hex: string }[];
  socials: { id: string; network: string; url: string }[];
  pageTypes: { id: string; pageType: string; urlPattern: string; targetWords: number | null }[];
}

interface ColourDraft {
  role: string;
  hex: string;
}

interface SocialDraft {
  network: string;
  url: string;
}

interface PageTypeDraft {
  pageType: string;
  urlPattern: string;
  targetWords: number | '';
}

export function BrandComposition({
  brandId,
  colors,
  socials,
  pageTypes,
  sections = ['colors', 'socials', 'pageTypes'],
}: BrandCompositionProps): React.JSX.Element {
  const shows = (section: 'colors' | 'socials' | 'pageTypes'): boolean =>
    sections.includes(section);
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const canWrite = useCan('databases', 'write');

  /**
   * `null` — «оператор не чіпав», тобто показуємо те, що прийшло з сервера. Без цього
   * розрізнення чернетка або затирала свіжі дані після збереження, або не давала прибрати
   * останній рядок: порожній масив і «немає чернетки» виглядали б однаково.
   */
  const [colourDraft, setColourDraft] = useState<ColourDraft[] | null>(null);
  const [socialDraft, setSocialDraft] = useState<SocialDraft[] | null>(null);
  const [pageDraft, setPageDraft] = useState<PageTypeDraft[] | null>(null);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.brands.pathKey() });
  };

  const saveColors = useMutation(
    trpc.brands.colors.mutationOptions({
      onSuccess: async () => {
        setColourDraft(null);
        await refresh();
      },
    }),
  );
  const saveSocials = useMutation(
    trpc.brands.socials.mutationOptions({
      onSuccess: async () => {
        setSocialDraft(null);
        await refresh();
      },
    }),
  );
  const savePageTypes = useMutation(
    trpc.brands.pageTypes.mutationOptions({
      onSuccess: async () => {
        setPageDraft(null);
        await refresh();
      },
    }),
  );

  const currentColours: ColourDraft[] =
    colourDraft ?? colors.map((colour) => ({ role: colour.role, hex: colour.hex }));
  const currentSocials: SocialDraft[] =
    socialDraft ?? socials.map((social) => ({ network: social.network, url: social.url }));
  const currentPages: PageTypeDraft[] =
    pageDraft ??
    pageTypes.map((entry) => ({
      pageType: entry.pageType,
      urlPattern: entry.urlPattern,
      targetWords: entry.targetWords ?? '',
    }));

  return (
    <Stack gap="md" data-testid="brand-composition">
      {!shows('colors') ? null : (
      <Box>
        <Text className={classes.dim} mb={6}>
          Кольори — один на роль; вага у відсотку готовності 10
        </Text>
        <Stack gap={4}>
          {currentColours.map((colour, index) => (
            <Group key={`${colour.role}-${String(index)}`} gap={8} data-testid={`brand-colour-${colour.role}`}>
              <Select
                size="xs"
                allowDeselect={false}
                data={[...BRAND_COLOR_ROLES]}
                value={colour.role}
                disabled={!canWrite}
                data-testid={`brand-colour-role-${String(index)}`}
                style={{ width: 130 }}
                onChange={(value) => {
                  setColourDraft(
                    currentColours.map((each, i) =>
                      i === index ? { ...each, role: value ?? each.role } : each,
                    ),
                  );
                }}
              />
              <TextInput
                size="xs"
                placeholder="#1A2B3C"
                value={colour.hex}
                disabled={!canWrite}
                data-testid={`brand-colour-hex-${String(index)}`}
                style={{ width: 120 }}
                onChange={(event) => {
                  const { value } = event.currentTarget;
                  setColourDraft(
                    currentColours.map((each, i) => (i === index ? { ...each, hex: value } : each)),
                  );
                }}
              />
              {canWrite ? (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  data-testid={`brand-colour-remove-${String(index)}`}
                  onClick={() => {
                    setColourDraft(currentColours.filter((_, i) => i !== index));
                  }}
                >
                  прибрати
                </Button>
              ) : null}
            </Group>
          ))}
        </Stack>
        {canWrite ? (
          <Group gap="sm" mt={6}>
            <Button
              size="compact-xs"
              variant="default"
              data-testid="brand-colour-add"
              onClick={() => {
                setColourDraft([...currentColours, { role: 'primary', hex: '#000000' }]);
              }}
            >
              + колір
            </Button>
            <Button
              size="compact-xs"
              loading={saveColors.isPending}
              data-testid="brand-colours-save"
              onClick={() => {
                saveColors.mutate({
                  id: brandId,
                  colors: currentColours.map((colour) => ({
                    role: colour.role as (typeof BRAND_COLOR_ROLES)[number],
                    hex: colour.hex,
                  })),
                });
              }}
            >
              Зберегти кольори
            </Button>
          </Group>
        ) : null}
        {saveColors.error === null ? null : (
          <Text className={classes.danger} mt={6} data-testid="brand-colours-error" role="alert">
            {publicMessageOf(saveColors.error)}
          </Text>
        )}
      </Box>
      )}

      {!shows('socials') ? null : (
      <Box>
        <Text className={classes.dim} mb={6}>
          Соцмережі — у відсоток готовності не входять, але їх рендерить блок футера
        </Text>
        <Stack gap={4}>
          {currentSocials.map((social, index) => (
            <Group key={`${social.network}-${String(index)}`} gap={8} data-testid={`brand-social-${String(index)}`}>
              <TextInput
                size="xs"
                placeholder="x"
                value={social.network}
                disabled={!canWrite}
                data-testid={`brand-social-network-${String(index)}`}
                style={{ width: 110 }}
                onChange={(event) => {
                  const { value } = event.currentTarget;
                  setSocialDraft(
                    currentSocials.map((each, i) => (i === index ? { ...each, network: value } : each)),
                  );
                }}
              />
              <TextInput
                size="xs"
                placeholder="https://…"
                value={social.url}
                disabled={!canWrite}
                data-testid={`brand-social-url-${String(index)}`}
                style={{ width: 240 }}
                onChange={(event) => {
                  const { value } = event.currentTarget;
                  setSocialDraft(
                    currentSocials.map((each, i) => (i === index ? { ...each, url: value } : each)),
                  );
                }}
              />
              {canWrite ? (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  data-testid={`brand-social-remove-${String(index)}`}
                  onClick={() => {
                    setSocialDraft(currentSocials.filter((_, i) => i !== index));
                  }}
                >
                  прибрати
                </Button>
              ) : null}
            </Group>
          ))}
        </Stack>
        {canWrite ? (
          <Group gap="sm" mt={6}>
            <Button
              size="compact-xs"
              variant="default"
              data-testid="brand-social-add"
              onClick={() => {
                setSocialDraft([...currentSocials, { network: '', url: '' }]);
              }}
            >
              + мережа
            </Button>
            <Button
              size="compact-xs"
              loading={saveSocials.isPending}
              data-testid="brand-socials-save"
              onClick={() => {
                saveSocials.mutate({ id: brandId, socials: currentSocials });
              }}
            >
              Зберегти соцмережі
            </Button>
          </Group>
        ) : null}
        {saveSocials.error === null ? null : (
          <Text className={classes.danger} mt={6} data-testid="brand-socials-error" role="alert">
            {publicMessageOf(saveSocials.error)}
          </Text>
        )}
      </Box>
      )}

      {!shows('pageTypes') ? null : (
      <Box>
        <Text className={classes.dim} mb={6}>
          Типи сторінок — що взагалі генерується для сайтів бренду; вага 5
        </Text>
        <Stack gap={4}>
          {currentPages.map((entry, index) => (
            <Group key={`${entry.pageType}-${String(index)}`} gap={8} data-testid={`brand-page-type-${entry.pageType}`}>
              <Select
                size="xs"
                allowDeselect={false}
                searchable
                data={[...PAGE_TYPES]}
                value={entry.pageType}
                disabled={!canWrite}
                data-testid={`brand-page-type-kind-${String(index)}`}
                style={{ width: 150 }}
                onChange={(value) => {
                  setPageDraft(
                    currentPages.map((each, i) =>
                      i === index ? { ...each, pageType: value ?? each.pageType } : each,
                    ),
                  );
                }}
              />
              <TextInput
                size="xs"
                placeholder="/bonus/"
                value={entry.urlPattern}
                disabled={!canWrite}
                data-testid={`brand-page-type-url-${String(index)}`}
                style={{ width: 160 }}
                onChange={(event) => {
                  const { value } = event.currentTarget;
                  setPageDraft(
                    currentPages.map((each, i) => (i === index ? { ...each, urlPattern: value } : each)),
                  );
                }}
              />
              <NumberInput
                size="xs"
                placeholder="слів"
                min={0}
                value={entry.targetWords}
                disabled={!canWrite}
                data-testid={`brand-page-type-words-${String(index)}`}
                style={{ width: 100 }}
                onChange={(value) => {
                  setPageDraft(
                    currentPages.map((each, i) =>
                      i === index
                        ? { ...each, targetWords: typeof value === 'number' ? value : '' }
                        : each,
                    ),
                  );
                }}
              />
              {canWrite ? (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  data-testid={`brand-page-type-remove-${String(index)}`}
                  onClick={() => {
                    setPageDraft(currentPages.filter((_, i) => i !== index));
                  }}
                >
                  прибрати
                </Button>
              ) : null}
            </Group>
          ))}
        </Stack>
        {canWrite ? (
          <Group gap="sm" mt={6}>
            <Button
              size="compact-xs"
              variant="default"
              data-testid="brand-page-type-add"
              onClick={() => {
                setPageDraft([...currentPages, { pageType: 'home', urlPattern: '/', targetWords: '' }]);
              }}
            >
              + тип сторінки
            </Button>
            <Button
              size="compact-xs"
              loading={savePageTypes.isPending}
              data-testid="brand-page-types-save"
              onClick={() => {
                savePageTypes.mutate({
                  id: brandId,
                  pageTypes: currentPages.map((entry) => ({
                    pageType: entry.pageType as (typeof PAGE_TYPES)[number],
                    urlPattern: entry.urlPattern,
                    targetWords: entry.targetWords === '' ? null : entry.targetWords,
                  })),
                });
              }}
            >
              Зберегти типи
            </Button>
          </Group>
        ) : null}
        {savePageTypes.error === null ? null : (
          <Text className={classes.danger} mt={6} data-testid="brand-page-types-error" role="alert">
            {publicMessageOf(savePageTypes.error)}
          </Text>
        )}
      </Box>
      )}
    </Stack>
  );
}
